/**
 * BroadcastDiagnosticsPanel — production-safe Live Scan diagnostics surface.
 *
 * Renders a one-shot snapshot of the iOS ReplayKit / App Group state so a
 * TestFlight tester can confirm whether the Broadcast Upload Extension is
 * actually reaching the host app's App Group container.
 *
 * Why this exists: telemetry on TestFlight builds was reporting
 * `appGroupAvailable: false` with no easy way to inspect the underlying
 * status.json / debug.json / frames/ without a dev rebuild that enables
 * VITE_SCAN_DEBUG. The richer in-modal DebugPanel is dev-only because it
 * also shows raw screen-mirror frames; this panel deliberately shows only
 * JSON metadata (no image bytes) and is therefore safe in production.
 *
 * It is intentionally read-only from the user's perspective:
 *   - Tap "Read" to call BroadcastBridge.getDiagnostics().
 *   - Tap "Copy" to put the JSON on the clipboard so the tester can paste
 *     it into a bug report.
 *   - Tap "Clear frames" to wipe pending JPEGs in the App Group (useful when
 *     a stuck broadcast left frames behind).
 *
 * Gated behind a parent-controlled `open` flag: the Settings page hides it
 * behind a 5-tap unlock on the version label so it doesn't surface to a
 * regular trainer poking around.
 */
import { useState } from "react";
import { broadcastBridge, type BroadcastDiagnostics } from "@/lib/broadcast-bridge";
import {
  getLastLiveScanFrameStats,
  type LiveScanFrameStats,
} from "@/lib/liveScanFrameStats";
import {
  readScanPipelineTelemetry,
  formatScanPipelineTelemetry,
  type ScanPipelineTelemetry,
} from "@/lib/scanPipelineTelemetry";

export function BroadcastDiagnosticsPanel() {
  const [diag, setDiag] = useState<BroadcastDiagnostics | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [cleared, setCleared] = useState<number | null>(null);
  const [frameStats, setFrameStats] = useState<LiveScanFrameStats | null>(null);
  const [pipelineStats, setPipelineStats] = useState<ScanPipelineTelemetry | null>(null);

  async function read() {
    setBusy(true);
    try {
      const d = await broadcastBridge.getDiagnostics();
      setDiag(d);
      // The host-side consumption stats are written by LiveScanModal at
      // session end. Reading them here lets the tester confirm whether
      // the disk-backed frames the extension wrote ever made it through
      // the OCR pipeline. A scan that wrote 15 frames but consumed 0
      // means the JS host never drained the App Group container — the
      // exact failure the in-app diagnostic was previously silent on.
      setFrameStats(getLastLiveScanFrameStats());
      // E49: the pre-OCR gate / OCR pipeline self-report. This is what tells a
      // tester WHICH stage stalled when the overlay says "Scanning..." but the
      // body stays empty: frames received -> passed the gate -> reached OCR ->
      // produced a hit. A frames>0 / gate=0 reading pins the stall on the gate.
      setPipelineStats(readScanPipelineTelemetry());
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    if (!diag) return;
    try {
      const payload = {
        ...diag,
        ...(frameStats ? { hostFrameConsumption: frameStats } : {}),
        ...(pipelineStats ? { scanPipeline: pipelineStats } : {}),
      };
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard can be unavailable on web preview; tester can still long-press
      // and copy from the rendered <pre>.
    }
  }

  async function clearFrames() {
    setBusy(true);
    try {
      const n = await broadcastBridge.clearFrames();
      setCleared(n);
      // Refresh the snapshot so the next read reflects the wipe.
      const d = await broadcastBridge.getDiagnostics();
      setDiag(d);
      setTimeout(() => setCleared(null), 2000);
    } finally {
      setBusy(false);
    }
  }

  // One-line summary for the collapsed state. Mirrors the interpretation
  // ladder in BroadcastBridge.swift so the tester can glance and triage.
  const summary = (() => {
    if (!diag) return "Not read yet";
    if (diag.source === "wrapper-web") return "Web preview. No native bridge";
    if (diag.source === "wrapper-plugin-missing") return "Plugin not registered (rebuild / cap sync)";
    if (diag.source === "wrapper-call-threw") return `Native call threw: ${diag.error ?? "unknown"}`;
    if (!diag.appGroupAvailable) return "App Group unavailable. Check entitlements";
    if (diag.debugFileExists === false) return "Extension never launched (no debug.json)";
    const frames = diag.framesOnDisk ?? 0;
    const state = (diag.status as { state?: string } | undefined)?.state ?? "idle";
    return `OK · state=${state} · frames=${frames}`;
  })();

  // Second-line summary describing whether the host app actually drained
  // the App Group container during the most recent live-scan session. A
  // session that wrote frames to disk but consumed zero is the exact
  // pattern that was previously silent in this panel.
  const consumptionSummary = (() => {
    if (!frameStats) return null;
    const written = (diag?.status as { frameCount?: number } | undefined)?.frameCount;
    return (
      `Last scan: consumed ${frameStats.framesConsumed}, dispatched ${frameStats.framesDispatched}` +
      (typeof written === "number" ? ` (extension wrote ${written})` : "") +
      (frameStats.framesSkippedStale > 0 ? ` · stale skipped ${frameStats.framesSkippedStale}` : "") +
      (frameStats.framesConsumeFailed > 0 || frameStats.framesDecodeFailed > 0
        ? ` · errors ${frameStats.framesConsumeFailed + frameStats.framesDecodeFailed}`
        : "")
    );
  })();

  // E49: pre-OCR gate / OCR pipeline triage line. Reads as a stage chain so the
  // tester can self-report exactly where a "Scanning... but empty" session
  // stalled without a dev rebuild.
  const pipelineSummary = (() => {
    if (!pipelineStats) return null;
    return `Pipeline: ${formatScanPipelineTelemetry(pipelineStats)}`;
  })();

  return (
    <div
      className="rounded-2xl p-4 mb-3"
      style={{ background: "var(--surface-elev-1)", border: "1px solid var(--surface-elev-border)" }}
      data-testid="settings-broadcast-diagnostics"
    >
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-2">
        Live Scan diagnostics
      </p>
      <p className="text-[11px] text-muted-foreground leading-relaxed mb-1" data-testid="broadcast-diag-summary">
        {summary}
      </p>
      {consumptionSummary && (
        <p
          className="text-[11px] text-muted-foreground leading-relaxed mb-3"
          data-testid="broadcast-diag-consumption-summary"
        >
          {consumptionSummary}
        </p>
      )}
      {pipelineSummary && (
        <p
          className="text-[11px] text-muted-foreground leading-relaxed mb-3"
          data-testid="broadcast-diag-pipeline-summary"
        >
          {pipelineSummary}
        </p>
      )}
      {!consumptionSummary && !pipelineSummary && <div className="mb-2" />}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={read}
          disabled={busy}
          data-testid="button-broadcast-diag-read"
          className="rounded-xl px-3 py-1.5 text-[11px] font-semibold"
          style={{
            background: "var(--surface-elev-2)",
            border: "1px solid var(--surface-elev-border)",
            color: "hsl(var(--foreground))",
            opacity: busy ? 0.6 : 1,
          }}
        >
          {busy ? "Reading…" : "Read"}
        </button>
        <button
          type="button"
          onClick={copy}
          disabled={!diag || busy}
          data-testid="button-broadcast-diag-copy"
          className="rounded-xl px-3 py-1.5 text-[11px] font-semibold"
          style={{
            background: "var(--surface-elev-2)",
            border: "1px solid var(--surface-elev-border)",
            color: "hsl(var(--foreground))",
            opacity: !diag || busy ? 0.4 : 1,
          }}
        >
          {copied ? "Copied" : "Copy JSON"}
        </button>
        <button
          type="button"
          onClick={clearFrames}
          disabled={busy}
          data-testid="button-broadcast-diag-clear"
          className="rounded-xl px-3 py-1.5 text-[11px] font-semibold diag-button-danger"
          style={{ opacity: busy ? 0.6 : 1 }}
        >
          {cleared !== null ? `Cleared ${cleared}` : "Clear frames"}
        </button>
      </div>
      {diag && (
        <pre
          className="mt-3 rounded-xl p-3 text-[10px] leading-snug overflow-x-auto diag-code"
          style={{ maxHeight: 320 }}
          data-testid="broadcast-diag-json"
        >
          {JSON.stringify(
            {
              ...diag,
              ...(frameStats ? { hostFrameConsumption: frameStats } : {}),
              ...(pipelineStats ? { scanPipeline: pipelineStats } : {}),
            },
            null,
            2,
          )}
        </pre>
      )}
    </div>
  );
}
